import React, { useState } from "react";
import "./Buttons.scss";
import { useHistory, useLocation } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import {
  fetchExamInstructionRequest,
  setTestDetails,
  setTestId,
  setTestType,
  setSelectedSetId,
  setPaperId,
} from "../../../../../redux/exam/examSlice";
import GameficationMode from "../../../../tests/Dashboard/GameficationMode";
import { resolveAttemptId } from "../../../utils/resolveAttemptId";
import { MISTAKE_BOOK_PRACTICE_SETS_ANALYTICS_PATH } from "../../../../MistakeBook/mistakeBookRoutes";
import isGetAnalyticsReportAitsStyle from "../../../utils/isGetAnalyticsReportAitsStyle";
import { isExactSummaryAvailable } from "../../../utils/isExactSummaryAvailable";

const Buttons = ({ hideReattempt: hideReattemptProp, hideSeeResponse: hideSeeResponseProp }) => {
  const history = useHistory();
  const location = useLocation();
  const dispatch = useDispatch();
  const { testDetails, testType } = useSelector((state) => state.examSlice);
  const { basicAnalytics, selectedAttemptId } = useSelector(
    (state) => state.analyticsSlice
  );
  const [showGameMode, setShowGameMode] = useState(false);
  const hideReattempt =
    hideReattemptProp ??
    isGetAnalyticsReportAitsStyle({
      testDetails,
      testType,
      routeState: location.state,
    });
  const hideSeeResponse =
    hideSeeResponseProp ??
    !isExactSummaryAvailable(basicAnalytics?.data);

  /**
   * Open gamification modal only when timed test attempts are exhausted.
   * Backend sets `attempt_message` in that case (same red copy as on the test card).
   */
  const shouldOpenPracticeModeModal = () =>
    Boolean(
      testDetails?.attempt_message &&
        String(testDetails.attempt_message).trim() !== ""
    );

  const handleGameModeConfirm = ({ selectedMode, gamificationOn }) => {
    const payload = {
      ...testDetails,
      practiceMode: selectedMode,
      gamificationOn,
      forceFreshStart: true,
      isResume: false,
    };

    const storageId = payload.setId ?? payload.testId ?? payload.paperId;
    if (storageId != null && storageId !== "") {
      localStorage.setItem(
        `exam_mode_${storageId}`,
        payload.practiceMode || "test"
      );
      localStorage.setItem(
        `exam_gamification_${storageId}`,
        payload.gamificationOn ? "1" : "0"
      );
    }

    dispatch(setTestId(payload.testId));
    dispatch(setTestType({ id: payload.paperType }));
    dispatch(setSelectedSetId(payload.setId ?? null));
    if (payload.paperId != null && payload.paperId !== undefined) {
      dispatch(setPaperId(payload.paperId));
    }
    dispatch(setTestDetails(payload));
    dispatch(fetchExamInstructionRequest(payload));
    setShowGameMode(false);
    history.push("/TestInstruction");
  };

  const handleReattempt = () => {
    if (!testDetails?.paperId && !testDetails?.setId) return;

    if (shouldOpenPracticeModeModal()) {
      setShowGameMode(true);
      return;
    }

    const payload = {
      ...testDetails,
      forceFreshStart: true,
      isResume: false,
      attempt_count: 0,
    };
    dispatch(setTestDetails(payload));
    dispatch(fetchExamInstructionRequest(payload));

    history.push("/TestInstruction");
  };

  const handleSeeResponse = () => {
    const attemptCount = resolveAttemptId(
      selectedAttemptId ?? basicAnalytics?.data?.selected_attempt,
      basicAnalytics?.data?.attempts || []
    );
    const isMistakeBookPracticeSet =
      testDetails?.source === "mistake_book_practice_set" ||
      location.pathname === MISTAKE_BOOK_PRACTICE_SETS_ANALYTICS_PATH ||
      location.state?.fromMistakeBookPracticeSet === true;

    history.push("/adv-analytics", {
      tab: "questionwise",
      attempt_count: attemptCount,
      ...(isMistakeBookPracticeSet
        ? {
            questionWiseOnly: true,
            fromMistakeBookPracticeSet: true,
            analyticsBackPath: MISTAKE_BOOK_PRACTICE_SETS_ANALYTICS_PATH,
            analyticsBackState: location.state || {},
          }
        : {}),
    });
  };

  if (hideReattempt && hideSeeResponse) {
    return null;
  }

  return (
    <div
      className={`basicAnalytics__buttons${
        hideReattempt || hideSeeResponse ? " basicAnalytics__buttons--single" : ""
      }`}
    >
      {!hideReattempt ? (
        <div
          className="basicAnalytics__buttons__button"
          onClick={handleReattempt}
        >
          Reattempt
        </div>
      ) : null}

      {!hideSeeResponse ? (
        <div
          className="basicAnalytics__buttons__button"
          onClick={handleSeeResponse}
        >
          See Response
        </div>
      ) : null}

      <GameficationMode
        isOpen={showGameMode}
        onClose={() => setShowGameMode(false)}
        onConfirm={handleGameModeConfirm}
        attemptMessage={testDetails?.attempt_message}
        testName={testDetails?.test_name || testDetails?.set_name || ""}
      />
    </div>
  );
};

export default Buttons;
